import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { startFlowCorrelationId } from "@/api";

/**
 * Top-level cashier flow segment from a hash-router pathname.
 * e.g. "/deposit/accounts" -> "deposit", "/withdraw/summary" -> "withdraw".
 */
const getFlowSegment = (pathname: string): string =>
  pathname.split("/").filter(Boolean)[0] ?? "";

/**
 * Terminal ("thank you") routes: deposit/withdraw/transfer completion pages and
 * the withdrawal error page. Requests made ON these pages still belong to the
 * finished workflow; leaving one means the next requests start a new workflow.
 */
const isTerminalPath = (pathname: string): boolean =>
  pathname.includes("/completion") || pathname.includes("/error-page");

/**
 * Generates a fresh X-Correlation-ID whenever the user enters a new top-level
 * flow (deposit, withdraw, transfer, history) OR re-enters a flow from a
 * completion/thank-you page (e.g. "Make another transfer", browser back).
 * Every API request made within that workflow then shares the same correlation
 * id (wired in src/api/index.ts).
 */
export const useFlowCorrelationId = () => {
  const { pathname } = useLocation();
  const flowSegment = getFlowSegment(pathname);
  const currentFlowRef = useRef<string | undefined>(undefined);
  const wasTerminalRef = useRef(false);

  const enteredNewFlow =
    Boolean(flowSegment) && flowSegment !== currentFlowRef.current;
  const leftCompletionPage =
    wasTerminalRef.current && !isTerminalPath(pathname);

  if (enteredNewFlow || leftCompletionPage) {
    currentFlowRef.current = flowSegment;
    startFlowCorrelationId();
  }
  wasTerminalRef.current = isTerminalPath(pathname);
};
